import React from 'react';

import { Box, Flex, Skeleton, Stack } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import {
  setCurrentCompetencyQuestionIndex,
  setCurrentMLInputSelectedQuestionId,
  useCurrentCompetencyQuestionIndex,
  useCurrentMLInputSelectedQuestionId,
  useIsValidateForm,
} from 'reducers/reviewFormSlice';
import MLCommentTextGenerationCompV1 from 'screens/common/MLCommentTextGenerationCompV1/MLCommentTextGenerationCompV1';
import { checkRequiredValidationOK } from 'screens/performanceReview/helpers';
import { useReviewPreviewParams } from 'screens/performanceReview/hooks/reviewPreviewHooks';
import useReviewFormParams from 'screens/performanceReview/hooks/useReviewFormParams';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';

import { Competency } from 'assets/assetsV1/iconComponents';

import { isMobileView } from 'constants/isMobileView';

import { updateQuestionResponse } from 'services/performanceReview';

import { AccordionButtonV2, AccordionItemV2, AccordionPanelV2, AccordionV2 } from 'uiToolkitV1/AccordionV2';
import { Paper } from 'uiToolkitV1/Paper';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { QuestionRenderer, ViewOnlyQuestionRenderer } from '../QuestionRenderer/QuestionRenderer';
import QuestionsNavigationFooter from '../QuestionsNavigationFooter/QuestionsNavigationFooter';
import ScrollToTopComp from '../ScrollToTopComp';
import { useReviewFormSidebarDataOld } from '../Sidebar/hooks/dataHooks';
import { QuestionResponseStatusTag } from '../newFormComponents/QuestionResponseStatusTag';
import CompetencyScrollableNav from './components/CompetencyScrollableNav';
import { useCompetencyFormData, useCompetencyFormMutation } from './hooks/dataHooks';

const CompetencyForm = () => {
  const { employeeId, providerId, reviewId } = useReviewFormParams();
  const isValidateForm = useIsValidateForm();
  const { data: formData = {} } = useReviewFormSidebarDataOld({
    employeeId,
    providerId,
    reviewId,
  });
  const revieweeEmployee = formData?.employee;

  const { isPreview } = useReviewPreviewParams();

  const loggedInEmployee = useSelector(selectLoggedInEmployeeData);
  const currentMLInputSelectedQuestionId = useCurrentMLInputSelectedQuestionId();

  const dispatch = useDispatch();
  const currentQuestionIndex = useCurrentCompetencyQuestionIndex();

  const { data, isLoading } = useCompetencyFormData({
    employeeId,
    providerId,
    reviewId,
  });

  const formMutation = useCompetencyFormMutation({ answerQuestion: updateQuestionResponse });

  const { competencyQuestions } = data ?? {};

  const setCurrentIndex = ({ index }) => {
    dispatch(setCurrentCompetencyQuestionIndex(index !== currentQuestionIndex ? index : -1));
  };

  const answerQuestionCurried =
    ({
      // eslint-disable-next-line no-shadow
      reviewId,
      // eslint-disable-next-line no-shadow
      employeeId,
      competencyId,
      questionId,
      // existing answer values
      // as BE doesn't support partial updates
      // we've to pass existing values when answering
      currentFeedBackText,
      currentFeedBackTextHtml,
      currentSelectedAnswerOptions,
      competencyIndex,
      questionIndex,
    }) =>
    async ({
      feedbackText = currentFeedBackText,
      feedbackTextHtml = currentFeedBackTextHtml,
      selectedAnswerOptions = currentSelectedAnswerOptions,
      questionIndex1 = questionIndex,
      questionId1 = questionId,
    }) => {
      formMutation.mutate({
        reviewId,
        competencyId,
        employeeId,
        questionId: questionId1,
        feedbackText,
        feedbackTextHtml,
        selectedAnswerOptions,
        sourceForm: 'Competency form',
        competencyIndex,
        questionIndex: questionIndex1,
        isPreview,
      });
    };

  if (isLoading) {
    return (
      <Stack>
        <Skeleton height="20px" />
        <Skeleton height="20px" />
        <Skeleton height="20px" />
      </Stack>
    );
  }
  return (
    <Paper
      p="0"
      w="100%"
      style={{
        boxShadow: '0px 0px 2px 0px rgba(0, 0, 0, 0.24)',
      }}
    >
      <AccordionV2 openPanelIndex={currentQuestionIndex}>
        {competencyQuestions?.map(({ tag, questions }, compIndex) => {
          if (currentMLInputSelectedQuestionId) {
            const currentMLSelectedQuestionIndex = questions.findIndex(
              (question) => question.id === currentMLInputSelectedQuestionId
            );
            const currentMLSelectedQuestion = questions?.[currentMLSelectedQuestionIndex] ?? null;
            if (
              currentMLSelectedQuestion &&
              currentMLSelectedQuestion?.isViewOnlyModeEnabled !== true &&
              currentMLSelectedQuestion.enableFeedbackText &&
              currentQuestionIndex === compIndex
            ) {
              return (
                // eslint-disable-next-line react/no-array-index-key
                <AccordionItemV2 key={compIndex}>
                  <AccordionPanelV2>
                    <Paper p="0" w="100%" flexGrow={1}>
                      <MLCommentTextGenerationCompV1
                        reviewId={reviewId}
                        questionId={currentMLSelectedQuestion.id}
                        revieweeId={revieweeEmployee?.id}
                        revieweeDisplayName={revieweeEmployee?.displayName}
                        reviewerId={loggedInEmployee.id}
                        reviewerDisplayName={loggedInEmployee.displayName}
                        onChange={answerQuestionCurried({
                          competencyId: tag?.id,
                          employeeId,
                          reviewId,
                          questionId: currentMLSelectedQuestion?.id,
                          currentFeedBackText: currentMLSelectedQuestion?.feedbackText,
                          currentFeedBackTextHtml: currentMLSelectedQuestion?.feedbackTextHtml,
                          currentSelectedAnswerOptions: currentMLSelectedQuestion?.selectedAnswerOptions,
                          competencyIndex: compIndex,
                          questionIndex: currentMLSelectedQuestionIndex,
                        })}
                        onClose={() => dispatch(setCurrentMLInputSelectedQuestionId(null))}
                        textHtml={currentMLSelectedQuestion.feedbackTextHtml}
                        plainText={currentMLSelectedQuestion.feedbackText}
                      />
                    </Paper>
                  </AccordionPanelV2>
                </AccordionItemV2>
              );
            }
          }

          return (
            <AccordionItemV2
              isError={!checkRequiredValidationOK({ questions, isValidateForm })}
              // eslint-disable-next-line react/no-array-index-key
              key={compIndex}
            >
              <AccordionButtonV2 onClick={() => setCurrentIndex({ index: compIndex })}>
                <Flex justifyContent="space-between" alignItems="center" flexGrow={1}>
                  <Flex alignItems="center" flexGrow={1} mr={4}>
                    <Competency color="functional.AF5C20" boxSize="24px" fill="transparent" mr={2} />
                    <TextBodyMedium bold>{tag?.displayName}</TextBodyMedium>
                  </Flex>
                  <Flex>
                    <QuestionResponseStatusTag
                      questions={questions}
                      isValidateForm={isValidateForm}
                      isFormFillingPage
                    />
                  </Flex>
                </Flex>
              </AccordionButtonV2>
              <AccordionPanelV2>
                <Box position="relative" flexGrow={1} maxH="500px" w="100%">
                  <Flex p={isMobileView() ? '0px' : '16px 16px 16px 0'} maxH="500px">
                    {!isMobileView() && (
                      <CompetencyScrollableNav
                        competencyId={tag?.id}
                        competencyName={tag?.name}
                        questions={questions}
                        answerQuestionHandler={answerQuestionCurried({
                          employeeId,
                          reviewId,
                          competencyId: tag?.id,
                          competencyIndex: compIndex,
                        })}
                        reviewId={reviewId}
                        revieweeId={revieweeEmployee?.id}
                        isManagerAISummaryEnabled={currentQuestionIndex === compIndex}
                        providerType={formData?.review?.providerType}
                      />
                    )}
                    <Flex
                      flexDir="column"
                      w={isMobileView() ? '100%' : '50%'}
                      p={4}
                      maxW={isMobileView() ? '100%' : '50%'}
                    >
                      <Flex flexDir="column" overflowY="auto" flexGrow={1}>
                        {questions?.map((question, index) => {
                          if (question.isViewOnlyModeEnabled === true)
                            return (
                              <ViewOnlyQuestionRenderer
                                {...question}
                                showQuestionText
                                questionIndex={index}
                                showQuestionIndex={questions.length >= 1}
                                showHeading={index === 0 || !questions[index - 1].isViewOnlyModeEnabled}
                              />
                            );
                          return (
                            <QuestionRenderer
                              answerQuestionHandler={answerQuestionCurried({
                                employeeId,
                                reviewId,
                                competencyId: tag?.id,
                                questionId: question?.id,
                                currentFeedBackText: question?.feedbackText,
                                currentFeedBackTextHtml: question?.feedbackTextHtml,
                                currentSelectedAnswerOptions: question?.selectedAnswerOptions,
                                competencyIndex: compIndex,
                                questionIndex: index,
                              })}
                              showQuestionText
                              questionIndex={index}
                              showQuestionIndex={questions.length >= 1}
                              {...question}
                              question={question}
                            />
                          );
                        })}
                      </Flex>
                      <QuestionsNavigationFooter questions={competencyQuestions} />
                    </Flex>
                    <ScrollToTopComp
                      isVisible={currentQuestionIndex === compIndex}
                      currentQuestionIndex={currentQuestionIndex}
                    />
                  </Flex>
                </Box>
              </AccordionPanelV2>
            </AccordionItemV2>
          );
        })}
      </AccordionV2>
    </Paper>
  );
};

export default CompetencyForm;
